<template>
  <div class="page-outer">
    <div class="page-header page-header-postback-editor">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯 Postback 規則' : '新增 Postback 規則' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="goBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> 儲存中...</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <div class="postback-info-banner">
      <i class="ri-information-line"></i>
      <div>
        <strong>Postback 回應</strong>說明：當用戶點擊 Flex／Template 訊息中帶有 <code>postback</code> 的按鈕時，系統會比對規則並自動回覆。
        建立規則後請至 <strong>LINE 訊息範本</strong>編輯按鈕，選擇「Postback」並從清單<strong>點選此規則</strong>，即可自動帶入觸發設定（無需手動輸入代碼）。
      </div>
    </div>

    <div v-if="pageLoading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <template v-else>
      <!-- 手機版預覽開關 -->
      <div class="mobile-preview-toggle-bar">
        <button class="mobile-preview-toggle-btn" @click="mobilePreviewOpen = !mobilePreviewOpen">
          <i :class="mobilePreviewOpen ? 'ri-eye-off-line' : 'ri-eye-line'"></i>
          {{ mobilePreviewOpen ? '關閉預覽' : '開啟訊息預覽' }}
        </button>
      </div>

      <div v-if="mobilePreviewOpen" class="mobile-preview-drawer">
        <div class="mobile-preview-header">
          <span>
            <i class="ri-smartphone-line"></i> 訊息預覽
            <span v-if="allPreviewMessages.length > 1" class="preview-count-badge">共 {{ allPreviewMessages.length }} 則</span>
          </span>
          <button class="mobile-preview-close" @click="mobilePreviewOpen = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="mobile-preview-body">
          <LineMessagePreview :messages="allPreviewMessages" empty-text="請從左側選擇訊息範本" />
        </div>
      </div>

      <!-- 雙欄佈局 -->
      <div class="editor-layout">
        <!-- 左側：表單 -->
        <form class="editor-form" @submit.prevent="handleSubmit">

          <!-- 觸發設定 -->
          <div class="form-card">
            <h3 class="card-title">觸發設定</h3>

            <div class="form-group">
              <label class="required">規則名稱</label>
              <input
                v-model="form.ruleName"
                type="text"
                class="form-control"
                placeholder="請輸入方便識別的規則名稱"
                maxlength="100"
                required
              />
            </div>

            <div class="form-group">
              <label>點選時顯示文字</label>
              <input
                v-model="form.displayText"
                type="text"
                class="form-control"
                placeholder="選填，例如：我要查詢優惠"
                maxlength="300"
              />
              <span class="form-text">有填寫會在好友點選按鈕時，先於對話中顯示此文字，再回應內容</span>
            </div>

            <div class="form-group">
              <label>啟用</label>
              <label class="switch">
                <input type="checkbox" v-model="form.isActive" />
                <span class="slider"></span>
              </label>
            </div>
          </div>

          <!-- 回覆設定（1-5 個範本） -->
          <div class="form-card">
            <div class="reply-card-header">
              <div>
                <h3 class="card-title" style="margin:0 0 4px">回覆訊息</h3>
                <p class="card-hint" style="margin:0">可設定 1–5 則訊息，依序發送給用戶</p>
              </div>
            </div>

            <!-- 訊息 slot 列表 -->
            <div class="msg-slots">
              <div
                v-for="(slot, idx) in selectedTemplates"
                :key="slotKeys[idx]"
                class="msg-slot"
                :class="{ active: activePreviewIdx === idx }"
              >
                <div class="slot-header" @click="activePreviewIdx = idx">
                  <div class="slot-number">{{ idx + 1 }}</div>
                  <div class="slot-title">
                    <span v-if="slot">{{ slot.name }}</span>
                    <span v-else class="slot-empty-label">未選擇</span>
                    <span v-if="slot" class="type-badge" :class="`type-${slot.messageType}`">{{ msgTypeLabel(slot.messageType) }}</span>
                  </div>
                  <div v-if="selectedTemplates.length > 1" class="slot-order-btns" @click.stop>
                    <button
                      type="button"
                      class="slot-order-btn"
                      :disabled="idx === 0"
                      title="上移"
                      @click="moveSlot(idx, -1)"
                    ><i class="ri-arrow-up-s-line"></i></button>
                    <button
                      type="button"
                      class="slot-order-btn"
                      :disabled="idx === selectedTemplates.length - 1"
                      title="下移"
                      @click="moveSlot(idx, 1)"
                    ><i class="ri-arrow-down-s-line"></i></button>
                  </div>
                  <button
                    v-if="selectedTemplates.length > 1"
                    type="button"
                    class="slot-remove-btn"
                    @click.stop="removeSlot(idx)"
                    title="移除此訊息"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>

                <!-- 展開：選擇器 -->
                <div v-if="activePreviewIdx === idx" class="slot-picker">
                  <LineMsgTemplatePicker
                    :ref="el => pickerRefs[idx] = el"
                    audience-filter="personal"
                    :model-value="slot"
                    @update:model-value="val => setSlotTemplate(idx, val)"
                  />
                </div>
              </div>
            </div>

            <button
              v-if="selectedTemplates.length < 5"
              type="button"
              class="btn-add-msg"
              @click="addSlot"
            >
              <i class="ri-add-circle-line"></i> 新增訊息（{{ selectedTemplates.length }}/5）
            </button>
            <div v-else class="hint-box hint-warning" style="margin-top:8px;font-size:13px;">
              <i class="ri-alert-line"></i> 已達最多 5 則訊息上限
            </div>
          </div>

          <!-- 匹配後貼標 -->
          <div class="form-card">
            <h3 class="card-title">匹配後貼標</h3>
            <p class="card-hint">Postback 命中且成功回覆後貼標。互動標籤（manual）重複觸發會累加濃度。</p>
            <div class="form-group">
              <TagSelectorMultiple
                v-model="form.onMatchTagIds"
                label="自動貼標"
                hint="選填，可複選"
              />
            </div>
          </div>

        </form>

        <!-- 右側：即時預覽（顯示全部訊息） -->
        <div class="preview-panel">
          <div class="preview-sticky">
            <div class="preview-title">
              <i class="ri-smartphone-line"></i>
              <span>訊息預覽</span>
              <span v-if="allPreviewMessages.length > 1" class="preview-count-badge">
                共 {{ allPreviewMessages.length }} 則
              </span>
            </div>
            <LineMessagePreview
              :messages="allPreviewMessages"
              empty-text="請從左側選擇訊息範本"
            />
          </div>
        </div>
      </div>
    </template>
    </div>
  </div>
</template>

<script>
import { postbackRuleService } from '@/api/modules/postbackRules.js';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';
import { showSuccess, showError } from '@/utils/swal.js';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import { toLineMsgTemplatePreviewData, MSG_TYPE_LABEL } from '@/utils/lineMsgTemplatePreview.js';

export default {
  name: 'PostbackRuleEditor',
  components: { LineMessagePreview, LineMsgTemplatePicker, TagSelectorMultiple },
  data() {
    return {
      pageLoading: false,
      submitting: false,
      mobilePreviewOpen: false,
      // 1-5 個範本的陣列（null 代表未選）
      selectedTemplates: [null],
      // 穩定 key（保持拖曳後 component 不會重建）
      slotKeys: [0],
      _keyCounter: 1,
      activePreviewIdx: 0,
      pickerRefs: [],
      form: {
        ruleName: '',
        displayText: '',
        isActive: true,
        onMatchTagIds: []
      }
    };
  },

  computed: {
    isEdit() {
      return !!this.$route.params.id;
    },

    // 將所有已選範本轉成 LineMessagePreview 的 messages 陣列格式
    allPreviewMessages() {
      return this.selectedTemplates
        .filter(t => t !== null)
        .map(t => this._toPreviewData(t));
    }
  },

  async mounted() {
    if (this.isEdit) {
      await this.loadRule();
    }
  },

  methods: {
    msgTypeLabel(type) {
      return MSG_TYPE_LABEL[type] || type;
    },

    _toPreviewData(t) {
      return toLineMsgTemplatePreviewData(t);
    },

    addSlot() {
      if (this.selectedTemplates.length < 5) {
        this.selectedTemplates.push(null);
        this.slotKeys.push(this._keyCounter++);
        this.activePreviewIdx = this.selectedTemplates.length - 1;
      }
    },

    removeSlot(idx) {
      this.selectedTemplates.splice(idx, 1);
      this.slotKeys.splice(idx, 1);
      if (this.activePreviewIdx >= this.selectedTemplates.length) {
        this.activePreviewIdx = this.selectedTemplates.length - 1;
      }
    },

    // ── 上下移動 ──────────────────────────────────────────
    moveSlot(idx, dir) {
      const toIdx = idx + dir;
      if (toIdx < 0 || toIdx >= this.selectedTemplates.length) return;

      const templates = [...this.selectedTemplates];
      const keys = [...this.slotKeys];
      [templates[idx], templates[toIdx]] = [templates[toIdx], templates[idx]];
      [keys[idx], keys[toIdx]] = [keys[toIdx], keys[idx]];
      this.selectedTemplates = templates;
      this.slotKeys = keys;

      // activePreviewIdx 跟著移
      if (this.activePreviewIdx === idx) this.activePreviewIdx = toIdx;
      else if (this.activePreviewIdx === toIdx) this.activePreviewIdx = idx;
    },

    setSlotTemplate(idx, val) {
      this.selectedTemplates.splice(idx, 1, val);
    },

    async loadRule() {
      this.pageLoading = true;
      try {
        const res = await postbackRuleService.get(this.$route.params.id);
        const rule = res.data;
        this.form = {
          ruleName: rule.ruleName || '',
          displayText: rule.displayText || '',
          isActive: rule.isActive !== undefined ? rule.isActive : true,
          onMatchTagIds: Array.isArray(rule.onMatchTagIds) ? [...rule.onMatchTagIds] : []
        };

        // 還原 templateIds → 直接批次查詢範本物件，塞入 selectedTemplates
        const ids = rule.templateIds || [];
        if (ids.length > 0) {
          const templates = await Promise.all(
            ids.map(id =>
              lineMsgTemplateService.getById(id)
                .then(res => res.data)
                .catch(() => null)
            )
          );
          this.selectedTemplates = templates;
          this.slotKeys = ids.map(() => this._keyCounter++);
        }
      } catch (e) {
        showError('載入規則失敗', e.response?.data?.message || '');
        this.goBack();
      } finally {
        this.pageLoading = false;
      }
    },

    _clientPostbackPlaceholder() {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return `postback:${crypto.randomUUID()}`;
      }
      return `postback:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    },

    async handleSubmit() {
      if (!this.form.ruleName.trim()) { showError('請輸入規則名稱', ''); return; }

      const templateIds = this.selectedTemplates
        .filter(t => t !== null)
        .map(t => t.id);

      if (templateIds.length === 0) {
        showError('請至少選擇一個回覆訊息範本', '');
        return;
      }

      this.submitting = true;
      try {
        const data = {
          ruleName: this.form.ruleName.trim(),
          displayText: this.form.displayText.trim() || null,
          templateIds,
          isActive: this.form.isActive,
          onMatchTagIds: Array.isArray(this.form.onMatchTagIds) ? this.form.onMatchTagIds : []
        };
        // 後端新版會忽略並自行產生；若 API 仍為舊版 schema 要求 postbackData，此欄位可通過驗證（畫面不顯示）
        if (!this.isEdit) {
          data.postbackData = this._clientPostbackPlaceholder();
        }
        if (this.isEdit) {
          await postbackRuleService.update(this.$route.params.id, data);
          await showSuccess('更新成功', '');
        } else {
          await postbackRuleService.create(data);
          await showSuccess('建立成功', '');
        }
        this.goBack();
      } catch (e) {
        showError('提交失敗', e.response?.data?.message || '');
      } finally {
        this.submitting = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'AutoReplyRules', query: { tab: 'postback' } });
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.page-container {
  padding-top: 72px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions .btn {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  color: #6b7280;
  transition: all 0.2s;
}
.btn-back:hover { border-color: #3b82f6; color: #3b82f6; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.postback-info-banner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  padding: 14px 16px;
  font-size: 13px;
  color: #1e40af;
  line-height: 1.7;
  margin-bottom: 4px;
}
.postback-info-banner i { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.postback-info-banner code {
  background: #dbeafe;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
}

/* 佈局：單欄，右側預覽浮動 */
.editor-layout {
  display: block;
}
.editor-form {
  margin-right: 344px;
}

/* 浮動預覽面板 */
.preview-panel { /* DOM 容器 */ }
.preview-sticky {
  position: fixed;
  top: 110px;
  right: 28px;
  width: 320px;
  max-height: calc(100vh - 130px);
  overflow-x: hidden;
  overflow-y: auto;
  z-index: 90;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  padding: 12px 0 0;
}
.preview-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  padding: 0 16px 12px;
  border-bottom: 1px solid #e5e7eb;
}
.preview-title i { font-size: 16px; color: #06C755; }

.postback-info-banner { margin-right: 344px; }

/* 表單 */
.editor-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
}
.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
}
.card-hint {
  font-size: 12px;
  color: #9ca3af;
  margin: -8px 0 12px 0;
}

.inline-group {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.form-group { margin-bottom: 16px; }
.form-group:last-child { margin-bottom: 0; }
.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
.form-control-narrow { max-width: 160px; }

.form-text { font-size: 12px; color: #9ca3af; margin-top: 4px; display: block; }
.form-text code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 11px; }

/* 回覆設定 Card */
.reply-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.btn-add-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 13px;
  border: 2px dashed #93c5fd;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: all .2s;
  box-sizing: border-box;
}
.btn-add-msg i { font-size: 18px; }
.btn-add-msg:hover { border-color: #3b82f6; color: #1d4ed8; background: #dbeafe; }
.btn-add-msg:disabled { opacity: .45; cursor: not-allowed; }

.hint-box { display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; }
.hint-warning { background: #fffbeb; color: #92400e; border: 1px solid #fcd34d; }

/* 訊息 slot */
.msg-slots { display: flex; flex-direction: column; gap: 8px; }

.msg-slot {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.15s;
}
.msg-slot.active { border-color: #3b82f6; }

.slot-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #f9fafb;
  cursor: pointer;
  user-select: none;
}
.msg-slot.active .slot-header { background: #eff6ff; }
.slot-order-btns {
  display: flex;
  flex-direction: row;
  gap: 2px;
  flex-shrink: 0;
}
.slot-order-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  color: #6b7280;
  font-size: 14px;
  padding: 1px 4px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.slot-order-btn:hover:not(:disabled) { border-color: #3b82f6; color: #3b82f6; }
.slot-order-btn:disabled { opacity: 0.3; cursor: not-allowed; }

.slot-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  flex-shrink: 0;
}
.msg-slot.active .slot-number {
  background: #3b82f6;
  color: #fff;
}

.slot-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  min-width: 0;
  overflow: hidden;
}
.slot-title > span:first-child {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.slot-empty-label { color: #9ca3af; font-weight: 400; font-style: italic; }

.type-badge {
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}
.type-badge.type-text     { background: #fef3c7; color: #b45309; }
.type-badge.type-image    { background: #dcfce7; color: #15803d; }
.type-badge.type-video    { background: #fce7f3; color: #be185d; }
.type-badge.type-flex,
.type-badge.type-flex_carousel { background: #ede9fe; color: #7c3aed; }
.type-badge.type-image_carousel { background: #e0f2fe; color: #0369a1; }
.type-badge.type-imagemap { background: #ffedd5; color: #c2410c; }

.slot-preview-btn,
.slot-remove-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
  color: #9ca3af;
}
.slot-preview-btn:hover { color: #3b82f6; }
.slot-preview-btn.is-active { color: #3b82f6; }
.slot-remove-btn:hover { color: #ef4444; }

.slot-picker { padding: 14px; background: #fff; border-top: 1px solid #e5e7eb; }

/* 預覽則數 badge */
.preview-count-badge {
  margin-left: auto;
  font-size: 11px;
  background: #dbeafe;
  color: #1d4ed8;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}

/* switch */
.switch { position: relative; display: inline-block; width: 44px; height: 24px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; inset: 0; background: #d1d5db; border-radius: 24px; transition: 0.2s; cursor: pointer; }
.slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  bottom: 3px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
input:checked + .slider { background: #3b82f6; }
input:checked + .slider::before { transform: translateX(20px); }


.btn { padding: 8px 20px; border-radius: 6px; font-size: 14px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.loading-state { text-align: center; padding: 60px; color: #9ca3af; }

/* 手機版預覽開關（桌機隱藏） */
.mobile-preview-toggle-bar { display: none; }
.mobile-preview-drawer { display: none; }

@media (max-width: 900px) {
  .postback-info-banner,
  .editor-form {
    margin-right: 0;
  }

  .page-container {
    padding-top: 80px;
  }
  .preview-panel,
  .preview-sticky {
    display: none;
  }

  .mobile-preview-toggle-bar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 12px;
  }
  .mobile-preview-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid #06C755;
    border-radius: 20px;
    background: #fff;
    color: #06C755;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all .15s;
  }
  .mobile-preview-toggle-btn:hover { background: #f0fff5; }

  .mobile-preview-drawer {
    display: block;
    margin-bottom: 12px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    background: #fff;
  }
  .mobile-preview-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  .mobile-preview-header i { color: #06C755; margin-right: 4px; }
  .mobile-preview-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: #f3f4f6;
    color: #6b7280;
    cursor: pointer;
    font-size: 16px;
  }
  .mobile-preview-close:hover { background: #e5e7eb; }
  .mobile-preview-body { padding: 16px; }
}
</style>

<style>
/* Postback 規則編輯頁固定 header */
.page-header-postback-editor {
  position: fixed;
  top: 56px;
  left: 260px;
  right: 0;
  z-index: 100;
}

.sidebar.collapsed ~ .main-content .page-header-postback-editor {
  left: 64px;
}

@media (max-width: 768px) {
  .page-header-postback-editor {
    left: 0 !important;
    top: 56px;
  }
}
</style>
